<template>
  <FullPage class="tasks" with-find @contextmenu.prevent @find="openFindBar">
    <template #topbar-left>
      <Breadcrumb :items="breadcrumbItems" />
    </template>
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-team-tasks"
      @close="closeFindBar"
    />
    <BaseNumberedHeader
      v-if="!isTitleScrolledPast && !needsRepair"
      title="Tasks"
    />
    <div id="scrollable-team-tasks" ref="containerEl" class="org-tasks">
      <div class="org-tasks-content">
        <template v-if="currentWorkspace">
          <div v-if="!needsRepair" class="toolbar">
            <BaseInputSelect
              v-if="workspaceOptions.length > 1"
              :key="`workspace-select-${selectRefresh}`"
              class="workspace-select"
              :value="currentWorkspace.workspaceId"
              :options="workspaceOptions"
              select-only
              @selected="selectWorkspace($event.value)"
            >
              <template #item="{ option }">
                <BaseInputSelectOptionItem :title="option.label">
                  <template v-if="!isWorkspaceAccessible(option.value)" #right>
                    <LockIcon class="lock-icon" />
                  </template>
                </BaseInputSelectOptionItem>
              </template>
            </BaseInputSelect>
            <BaseTabs
              v-model:selected-tab="selectedTab"
              :tabs-titles="tabsTitles"
              class="tabs"
              storage-key="tasks:org:tab"
            />
          </div>
          <ProvisionedRepair
            v-if="needsRepair"
            class="repair"
            :org-id="orgId"
            :repairing="isRepairing"
            :failed="repairFailed"
            @repair="repair"
          />
          <PageLoading
            v-else-if="currentWorkspace.loading"
            class="tasks-loading"
            :with-title="false"
          />
          <template v-else>
            <template v-if="selectedTab !== 'Upcoming'">
              <div class="sections">
                <TaskListSection
                  v-for="(section, index) in displayedSections"
                  :key="`${currentWorkspace.workspaceId}:${section.key}`"
                  ref="sectionEls"
                  :add-task="section.addTask"
                  :assignee="selectedTab !== 'Assigned'"
                  :avatar="section.avatar"
                  :collapsible="section.collapsible ?? true"
                  :container-el="containerEl"
                  :default-collapsed="section.defaultCollapsed ?? false"
                  :is-any-section-selected="isAnySectionSelected"
                  :loading="isTasksLoading"
                  :pagination="
                    // Unpaginated while the find bar is open, so the search
                    // covers every task, not just the loaded window.
                    isFindBarOpen || selectedTab === 'Assigned' ? undefined : 25
                  "
                  :section-index="index"
                  :sections-number="displayedSections.length"
                  :storage-key="`tasks:org:${currentWorkspace.workspaceId}:collapsed:${section.key}`"
                  :list-key="
                    orgSectionListKey(
                      orgId,
                      currentWorkspace.workspaceId,
                      section.key
                    )
                  "
                  :task-ids="section.taskIds"
                  :title="section.title"
                  @unselect-all-tasks="unselectAllTasks"
                />
                <BaseEmptyTasklist
                  v-if="
                    selectedTab === 'Assigned' &&
                    !isTasksLoading &&
                    displayedSections.length === 0
                  "
                  text="No task is assigned yet"
                />
              </div>
            </template>
            <template v-else>
              <Loader v-if="isTasksLoading" class="weeks-loading">
                <BaseSkeletonList is-compact :size="8" />
              </Loader>
              <div v-else ref="weeksEl" class="weeks">
                <UpcomingWeek
                  v-for="(entry, index) in upcomingWeeks"
                  :key="`org-upcoming-${currentWorkspace.workspaceId}-${entry.week.year}-${entry.week.n}`"
                  ref="weekEls"
                  :batch="entry.batch"
                  :container-el="containerEl"
                  :is-upcoming-selected="isUpcomingSelected"
                  :show-all="isFindBarOpen"
                  :task-list-el="weeksEl"
                  :week="entry.week"
                  :week-index="index"
                  :weeks-number="upcomingWeeks.length"
                  @unselect-all-tasks="unselectAllTasks"
                />
                <BaseEmptyTasklist
                  v-if="upcomingWeeks.length === 0"
                  text="Glance at all the tasks that are planned"
                />
              </div>
            </template>
            <div class="unselect-area" @click="unselectAllTasks" />
          </template>
        </template>
      </div>
    </div>
  </FullPage>
</template>
<script lang="ts">
import { isSameDay } from "date-fns";
import { match } from "ts-pattern";
import { computed, defineComponent, onBeforeUnmount, ref, watch } from "vue";

import controller from "@ctrl/controller";
import type { Batch, GeneralizedTaskId, Week } from "@ctrl/controller-rpc";

import BaseEmptyTasklist from "@/components/base/BaseEmptyTasklist.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseInputSelectOptionItem from "@/components/base/BaseInputSelectOptionItem.vue";
import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import BaseTabs from "@/components/base/BaseTabs.vue";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb.vue";
import { getDateFromWeek } from "@/components/dates";
import type { Personish } from "@/components/elements/Avatar.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import LockIcon from "@/components/icons/LockIcon.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import { openUpgradeModal } from "@/components/modals/upgrade";
import PageLoading from "@/components/PageLoading.vue";
import ProvisionedRepair from "@/components/ProvisionedRepair.vue";
import { UseSettings } from "@/components/settings/controller/settings";
import {
  orgSectionKey as orgSectionListKey,
  taskListKeys,
} from "@/components/tasks/ordering";
import TaskListSection from "@/components/tasks/TaskListSection.vue";
import type { OrgTask, OrgWorkspaceTasks } from "@/components/tasks/teamTasks";
import {
  groupAssignedTasksByMember,
  useTeamTasks,
} from "@/components/tasks/teamTasks";
import UpcomingWeek from "@/components/tasks/UpcomingWeek.vue";
import FullPage from "@/components/templates/FullPage.vue";
import {
  organizationWorkspaceSelectionStorageKey,
  WORKSPACES_ENTITLEMENT_KEY,
} from "@/components/workspaces";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { useProvisionedRepair } from "@/composables/useProvisionedRepair";
import { useReleaser } from "@/ref";

const { weekOfDate } = controller;

const TABS = ["All", "Unassigned", "Assigned", "Upcoming"] as const;
type TeamTasksTab = (typeof TABS)[number];

/** One retractable list of the screen (a status or an assignee). */
type SectionSpec = {
  key: string;
  title?: string;
  /** Member shown as an avatar next to the title (Assigned tab). */
  avatar?: Personish;
  taskIds: GeneralizedTaskId[];
  defaultCollapsed?: boolean;
  collapsible?: boolean;
  addTask?: (title: string) => Promise<GeneralizedTaskId | undefined>;
};

/**
 * Tasks screen of a (non-personal) organization: the tasks of one workspace
 * at a time (workspace selector), grouped by status (All), assignment
 * (Unassigned / Assigned) or planning (Upcoming).
 */
export default defineComponent({
  name: "TeamTasks",
  components: {
    BaseEmptyTasklist,
    BaseInputSelect,
    BaseInputSelectOptionItem,
    BaseNumberedHeader,
    BaseSkeletonList,
    BaseTabs,
    Breadcrumb,
    FindBar,
    FullPage,
    Loader,
    LockIcon,
    PageLoading,
    ProvisionedRepair,
    TaskListSection,
    UpcomingWeek,
  },
  props: {
    orgId: { type: String, required: true },
  },
  setup(props) {
    const controller = inject.controller();
    const profile = inject.profile();
    const clock = inject.clock();
    const billing = inject.billing();
    const { isWeekStartOnSunday } = UseSettings.getInstance();

    const releaser = useReleaser();

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const containerEl = ref<HTMLDivElement>();
    const sectionEls = ref<Array<InstanceType<typeof TaskListSection>>>([]);
    const weekEls = ref<Array<InstanceType<typeof UpcomingWeek>>>([]);
    // Passed down as each week's task-list element: up/down navigation
    // no-ops while it is unset (see selectUp/selectDown in useTaskList).
    const weeksEl = ref<HTMLDivElement>();

    // Title in the top bar once the numbered header is scrolled out of view.
    const isTitleScrolledPast = ref(false);
    let scrollCleanup: (() => void) | null = null;

    watch(
      containerEl,
      (el) => {
        scrollCleanup?.();
        scrollCleanup = null;
        isTitleScrolledPast.value = false;
        if (!el) return;

        const handler = () => {
          isTitleScrolledPast.value = el.scrollTop > 0;
        };
        el.addEventListener("scroll", handler);
        scrollCleanup = () => el.removeEventListener("scroll", handler);
      },
      { flush: "post" }
    );

    onBeforeUnmount(() => {
      scrollCleanup?.();
    });

    const breadcrumbItems = computed((): BreadcrumbItem[] => {
      const item: BreadcrumbItem = {
        title: "Tasks",
        link: { params: { id: "tasks" } },
        fadeIn: true,
        onClick: () => {
          containerEl.value?.scrollTo({ top: 0, behavior: "smooth" });
        },
      };
      return isTitleScrolledPast.value ? [item] : [];
    });

    const orgTasks = useTeamTasks(controller, profile, props.orgId);
    const workspaces = releaser.add(orgTasks.sections);

    // Entitlement: the organization's default workspace is always accessible,
    // additional workspaces require the "workspaces" entitlement. The screen
    // itself is always reachable; only switching is locked in the selector.
    const organization = computed(() =>
      profile.value.organizations.find((org) => org.id === props.orgId)
    );
    const defaultWorkspaceId = computed(
      () =>
        organization.value?.workspaces.find((workspace) => workspace.default)
          ?.id ?? organization.value?.workspaces[0]?.id
    );
    const isEntitledToWorkspaces = computed<boolean>(
      () =>
        billing.value?.isEntitledTo(WORKSPACES_ENTITLEMENT_KEY, props.orgId)
          ?.value ?? false
    );
    const isWorkspaceAccessible = (workspaceId: string): boolean =>
      isEntitledToWorkspaces.value || workspaceId === defaultWorkspaceId.value;
    const accessibleWorkspaces = computed<OrgWorkspaceTasks[]>(() =>
      workspaces.value.filter((workspace) =>
        isWorkspaceAccessible(workspace.workspaceId)
      )
    );

    // Selected workspace (persisted per organization).
    const { get, syncState } = useBrowserStore(profile.value.user.accountId);
    const defaultSelectedWorkspaceId = get(
      `tasks:org:${props.orgId}:workspace`,
      defaultWorkspaceId.value ?? workspaces.value[0]?.workspaceId ?? ""
    );
    const selectedWorkspaceId = releaser.add(
      syncState<string>(
        organizationWorkspaceSelectionStorageKey(props.orgId),
        defaultSelectedWorkspaceId
      )
    );
    // A prior selection of a now-locked workspace must not block the screen:
    // fall back to the default without opening the upgrade modal.
    if (
      selectedWorkspaceId.value &&
      !isWorkspaceAccessible(selectedWorkspaceId.value) &&
      defaultWorkspaceId.value
    ) {
      selectedWorkspaceId.value = defaultWorkspaceId.value;
    }
    const currentWorkspace = computed<OrgWorkspaceTasks | undefined>(
      () =>
        accessibleWorkspaces.value.find(
          (workspace) => workspace.workspaceId === selectedWorkspaceId.value
        ) ?? accessibleWorkspaces.value[0]
    );
    const currentWorkspaceMembers = computed(
      () =>
        organization.value?.workspaces.find(
          (workspace) => workspace.id === currentWorkspace.value?.workspaceId
        )?.members ?? {}
    );

    // Inspect the provisioned types of all the organization's workspaces:
    // when any is broken the whole screen becomes a Repair empty state,
    // even if the displayed workspace is unaffected. The Repair button
    // heals all the broken workspaces at once, then reloads the sections.
    const { needsRepair, isRepairing, repairFailed, repair } =
      useProvisionedRepair(
        controller,
        profile,
        () =>
          organization.value?.workspaces.map((workspace) => workspace.id) ?? [],
        () => orgTasks.reload()
      );

    // Bumped to re-render the selector when a locked selection is refused
    // (the select has already displayed the clicked option at that point).
    const selectRefresh = ref(0);
    const selectWorkspace = (workspaceId: string) => {
      if (!isWorkspaceAccessible(workspaceId)) {
        openUpgradeModal(billing, props.orgId, WORKSPACES_ENTITLEMENT_KEY);
        selectRefresh.value += 1;
        return;
      }
      unselectAllTasks();
      selectedWorkspaceId.value = workspaceId;
    };
    // Locked workspaces stay listed with a lock icon: selecting one opens
    // the upgrade modal without leaving the current (accessible) workspace.
    // Default workspace first, then the rest in the sections' order.
    const workspaceOptions = computed<SelectOption[]>(() => {
      const defaultId = defaultWorkspaceId.value;
      return [...workspaces.value]
        .sort((a, b) => {
          if (a.workspaceId === defaultId) return -1;
          if (b.workspaceId === defaultId) return 1;
          return a.workspaceName.localeCompare(b.workspaceName);
        })
        .map((workspace) => ({
          value: workspace.workspaceId,
          label: workspace.workspaceName,
          aliases: [],
        }));
    });

    const tabsTitles = TABS;
    const selectedTab = ref<TeamTasksTab>("All");
    // BaseTabs restores the persisted tab without checking it still exists:
    // fall back to "All" for values of removed tabs (e.g. the old "Board").
    watch(selectedTab, (tab) => {
      if (!TABS.includes(tab)) selectedTab.value = "All";
    });

    // A `reader` member of the displayed workspace cannot create tasks: the
    // sections then omit their add-task input ("Add task…").
    const canAddTasks = computed(() =>
      isWorkspaceWritable(profile.value, currentWorkspace.value?.workspaceId)
    );

    const loadedTasks = computed<OrgTask[] | undefined>(
      () => currentWorkspace.value?.tasks.value
    );
    const isTasksLoading = computed(() => loadedTasks.value === undefined);
    const tasks = computed<OrgTask[]>(() => loadedTasks.value ?? []);
    const idsWhere = (
      predicate: (task: OrgTask) => boolean
    ): GeneralizedTaskId[] =>
      tasks.value.filter(predicate).map((task) => task.id);

    const allSections = computed<SectionSpec[]>(() => [
      {
        key: taskListKeys.unplanned,
        title: "Todo",
        taskIds: idsWhere((task) => task.status === "todo"),
        addTask: canAddTasks.value
          ? currentWorkspace.value?.addTask
          : undefined,
      },
      {
        key: "done",
        title: "Completed",
        taskIds: idsWhere((task) => task.status === "done"),
        defaultCollapsed: true,
      },
      {
        key: "discarded",
        title: "Discarded",
        taskIds: idsWhere((task) => task.status === "discarded"),
        defaultCollapsed: true,
      },
    ]);

    const unassignedSections = computed<SectionSpec[]>(() => [
      {
        key: "unassigned",
        taskIds: idsWhere((task) => task.status === "todo" && !task.assigneeId),
        collapsible: false,
        addTask: canAddTasks.value
          ? currentWorkspace.value?.addTask
          : undefined,
      },
    ]);

    // One retractable list per workspace member, including members without
    // uncompleted assigned tasks.
    const assignedSections = computed<SectionSpec[]>(() =>
      groupAssignedTasksByMember(
        tasks.value,
        currentWorkspaceMembers.value
      ).map((group) => ({
        key: `assignee:${group.memberId}`,
        title: group.name,
        avatar: {
          kind: "member" as const,
          email: group.email,
          name: group.name,
        },
        taskIds: group.taskIds,
        // Tasks created from a member's section are assigned to them.
        addTask: canAddTasks.value
          ? (title: string) =>
              currentWorkspace.value?.addTask?.(title, group.assignee) ??
              Promise.resolve(undefined)
          : undefined,
      }))
    );

    const displayedSections = computed<SectionSpec[]>(() =>
      match(selectedTab.value)
        .with("All", () => allSections.value)
        .with("Unassigned", () => unassignedSections.value)
        .with("Assigned", () => assignedSections.value)
        .with("Upcoming", () => [])
        .exhaustive()
    );

    // Upcoming: the workspace's uncompleted scheduled tasks, as one batch per
    // week (current week onwards), like the personal Upcoming tab.
    const upcomingWeeks = computed<Array<{ week: Week; batch: Batch }>>(() => {
      const weekKey = (week: Week) => week.year * 100 + week.n;
      const currentKey = weekKey(weekOfDate(clock.value));
      const byWeek = new Map<number, { week: Week; tasks: OrgTask[] }>();
      for (const task of tasks.value) {
        if (task.status !== "todo" || !task.scheduled) continue;
        const week = weekOfDate(task.scheduled);
        const key = weekKey(week);
        if (key < currentKey) continue;
        const entry = byWeek.get(key) ?? { week, tasks: [] };
        entry.tasks.push(task);
        byWeek.set(key, entry);
      }
      return Array.from(byWeek.values())
        .sort((a, b) => weekKey(a.week) - weekKey(b.week))
        .map(({ week, tasks }) => ({
          week,
          batch: {
            unscheduled: [],
            scheduled: Array.from({ length: 7 }, (_, index) => {
              const day = getDateFromWeek(
                week,
                index,
                isWeekStartOnSunday.value
              );
              return {
                todo: tasks
                  .filter(
                    (task) => task.scheduled && isSameDay(task.scheduled, day)
                  )
                  .map((task) => task.id),
                other: [],
              };
            }),
          },
        }));
    });

    const isAnySectionSelected = computed(() =>
      sectionEls.value.map((el) => el.isSectionSelected).includes(true)
    );
    const isUpcomingSelected = computed(() =>
      weekEls.value.map((el) => el.isWeekSelected).includes(true)
    );
    const unselectAllTasks = () => {
      sectionEls.value.forEach((el) => el.unselectTask());
      weekEls.value.forEach((el) => el.unselectTask());
    };

    return {
      breadcrumbItems,
      closeFindBar,
      containerEl,
      currentWorkspace,
      displayedSections,
      findBarEl,
      isAnySectionSelected,
      isFindBarOpen,
      isRepairing,
      isTasksLoading,
      isTitleScrolledPast,
      openFindBar,
      isUpcomingSelected,
      isWorkspaceAccessible,
      needsRepair,
      orgSectionListKey,
      repair,
      repairFailed,
      sectionEls,
      selectedTab,
      selectRefresh,
      selectWorkspace,
      tabsTitles,
      unselectAllTasks,
      upcomingWeeks,
      weekEls,
      weeksEl,
      workspaceOptions,
    };
  },
});
</script>
<style scoped lang="scss">
$margin: 64px;

.tasks {
  .base-numbered-header {
    padding: 0 $margin;
    width: calc(100% - $margin * 2);
  }
  :deep(.content) {
    width: 100%;
    padding-inline-start: 0;
    padding-inline-end: 0;
    // Anchor for the find bar, which must not scroll with the list.
    position: relative;
  }
}

.org-tasks {
  height: 100%;
  margin-bottom: 16px;
  margin-inline-start: -12px;
  display: flex;
  flex-direction: column;
  padding: 0 $margin;
  max-width: calc(100% - $margin * 2);
  overflow-y: scroll;
  overflow: auto;

  .org-tasks-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 4px 12px 12px;

    .workspace-select {
      width: 220px;

      .lock-icon {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
        :deep(.stroke) {
          stroke: $red;
        }
      }
    }
  }

  .sections,
  .weeks {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    // Detach the first section from the sticky tabs toolbar.
    margin-top: 24px;
  }

  .tasks-loading,
  .weeks-loading {
    margin-top: 24px;
  }

  .repair {
    flex: 1;
  }

  .unselect-area {
    display: flex;
    flex: 1;
    min-height: 48px;
  }
}
</style>
